import {
  forwardRef, useState, useImperativeHandle, useRef,
} from 'react';
import {
  Modal, Form, Input, InputNumber, Cascader, Select, message,
} from 'antd';
import { func, any } from 'prop-types';
import { addDecimalNum } from '@/utils/utils';
import { getGrowerTownList, getGrowerVillageList } from '@/services/largeGrainGrower';

const formItemLayout = {
  labelCol: {
    sm: { span: 6 },
  },
  wrapperCol: {
    sm: { span: 24 },
  },
};

const FarmingForm = forwardRef((props, ref) => {
  const { dict } = props;
  const [form] = Form.useForm();
  const countrysideCode = Form.useWatch('countrysideCode', form);
  const [open, setOpen] = useState(false);
  const [disabled, setDisabled] = useState(false);
  const [provinceIdDisabled, setProvinceIdDisabled] = useState(false);
  const [originalAreaId, setOriginalAreaId] = useState(null); // 保存原始区域ID
  const [confirmModalOpen, setConfirmModalOpen] = useState(false); // 确认modal状态
  const [pendingValues, setPendingValues] = useState(null); // 待确认的表单值
  const [townOptions, setTownOptions] = useState([]);
  const [villageOptions, setVillageOptions] = useState([]);
  const [townLoading, setTownLoading] = useState(false);
  const [villageLoading, setVillageLoading] = useState(false);
  // 防止快速切换区域时，较早返回的请求覆盖最新下拉数据。
  const townRequestId = useRef(0);
  const villageRequestId = useRef(0);

  const loadTownOptions = async (cityCode, areaCode) => {
    const requestId = townRequestId.current + 1;
    townRequestId.current = requestId;
    if (!cityCode || !areaCode) {
      setTownOptions([]);
      return [];
    }

    setTownLoading(true);
    try {
      const list = await getGrowerTownList(cityCode, areaCode);
      if (requestId === townRequestId.current) {
        const options = Array.isArray(list) ? list : [];
        setTownOptions(options);
        return options;
      }
    } catch {
      if (requestId === townRequestId.current) setTownOptions([]);
    } finally {
      if (requestId === townRequestId.current) setTownLoading(false);
    }
    return [];
  };

  const loadVillageOptions = async (townCode) => {
    const requestId = villageRequestId.current + 1;
    villageRequestId.current = requestId;
    if (!townCode) {
      setVillageOptions([]);
      return [];
    }

    setVillageLoading(true);
    try {
      const list = await getGrowerVillageList(townCode);
      if (requestId === villageRequestId.current) {
        const options = Array.isArray(list) ? list : [];
        setVillageOptions(options);
        return options;
      }
    } catch {
      if (requestId === villageRequestId.current) setVillageOptions([]);
    } finally {
      if (requestId === villageRequestId.current) setVillageLoading(false);
    }
    return [];
  };

  const onCancel = () => {
    townRequestId.current += 1;
    villageRequestId.current += 1;
    setOpen(false);
    form.resetFields();
    setOriginalAreaId(null);
    setTownOptions([]);
    setVillageOptions([]);
    setTownLoading(false);
    setVillageLoading(false);
  };

  const onConfirmCancel = () => {
    setConfirmModalOpen(false);
    setPendingValues(null);
  };

  useImperativeHandle(ref, () => ({
    open: (data) => {
      townRequestId.current += 1;
      villageRequestId.current += 1;
      setOpen(true);
      setTownOptions([]);
      setVillageOptions([]);
      setTownLoading(false);
      setVillageLoading(false);

      if (data?.isAddMode) {
        setProvinceIdDisabled(true);
      } else if (data?.isEditMode) {
        setProvinceIdDisabled(false);
        // 在编辑模式下保存原始区域ID
        setOriginalAreaId(data?.areaId);
      } else {
        setProvinceIdDisabled(false);
        setOriginalAreaId(null);
      }

      if (data) {
        const targetAreaCode = data?.areaId && Number(data?.areaId);
        const isEthnicArea = dict.minorityArea?.some((item) => item.areaCode === targetAreaCode);
        if (isEthnicArea) {
          setDisabled(false);
        } else {
          form.setFieldsValue({
            potatoArea: undefined,
            buckwheatArea: undefined,
            highlandBarleyArea: undefined,
          });
          setDisabled(true);
        }
        form.setFieldsValue({
          ...data,
          provinceId: [data.provinceId, data.cityId, data.areaId],
        });

        // 编辑或追加时先按已选区域回填乡镇；编辑时再回填对应村列表。
        loadTownOptions(data.cityId, data.areaId).then((options) => {
          const hasSelectedTown = options.some(
            (item) => String(item.townCode) === String(data.countrysideCode),
          );
          if (hasSelectedTown) loadVillageOptions(data.countrysideCode);
        });
      } else {
        form.resetFields();
        setDisabled(false);
        setOriginalAreaId(null);
      }
    },
    close: onCancel,
  }));

  const handleSubmit = (values, shouldClearOtherData = false) => {
    const selectedTown = townOptions.find(
      (item) => String(item.townCode) === String(values.countrysideCode),
    );
    const selectedVillage = villageOptions.find(
      (item) => String(item.villageCode) === String(values.villageCode),
    );
    const val = {
      ...values,
      provinceId: values.provinceId[0],
      cityId: values.provinceId[1],
      areaId: values.provinceId[2],
      addressCountryside: selectedTown?.townName,
      addressVillage: selectedVillage?.villageName,
      shouldClearOtherData, // 传递是否需要清除其他数据的标志
    };
    props.onSubmit(val);
    form.resetFields();
    setOriginalAreaId(null);
  };

  const onConfirmOk = () => {
    const newAreaCode = Number(pendingValues.provinceId[2]);
    const isNewAreaEthnic = dict.minorityArea?.some((item) => item.areaCode === newAreaCode);
    const shouldClearOtherData = !isNewAreaEthnic; // 如果新区域不是民族地区，需要清除其他数据

    handleSubmit(pendingValues, shouldClearOtherData);
    setConfirmModalOpen(false);
    setPendingValues(null);
    setOpen(false);
  };

  const onOk = () => {
    form.validateFields().then((values) => {
      // 校验逻辑
      const contractedArea = values.contractedArea || 0;
      const leasedArea = values.leasedArea || 0;
      const totalLandArea = addDecimalNum([contractedArea, leasedArea]);

      const wheatArea = values.wheatArea || 0;
      const riceArea = values.riceArea || 0;
      const cornArea = values.cornArea || 0;
      const potatoArea = values.potatoArea || 0;
      const buckwheatArea = values.buckwheatArea || 0;
      const highlandBarleyArea = values.highlandBarleyArea || 0;

      // 校验1: 小麦的数字不能大于承包耕地面积+租种耕地面积的总和
      if (wheatArea > totalLandArea) {
        message.error('小麦的总亩数不能大于承包耕地面积与租种耕地面积的总和');
        return;
      }

      // 校验2: 水稻+玉米+马铃薯+荞麦+青稞的总和不能大于承包耕地面积+租种耕地面积的总和
      const otherCropsTotal = addDecimalNum([riceArea, cornArea, potatoArea, buckwheatArea, highlandBarleyArea]);
      if (otherCropsTotal > totalLandArea) {
        message.error('水稻、玉米、马铃薯、荞麦、青稞的总亩数不能大于承包耕地面积与租种耕地面积的总和');
        return;
      }

      // 检查是否为编辑模式且区域发生了变更
      const currentAreaId = values.provinceId[2];
      const isAreaChanged = originalAreaId && String(originalAreaId) !== String(currentAreaId);

      if (isAreaChanged) {
        // 区域发生变更，显示确认modal
        setPendingValues(values);
        setConfirmModalOpen(true);
      } else {
        // 区域未变更，直接提交
        handleSubmit(values);
        setOpen(false);
      }
    });
  };

  const onChange = (value, r) => {
    const targetAreaCode = value && Number(value[value.length - 1]);
    const isEthnicArea = dict.minorityArea?.some((item) => item.areaCode === targetAreaCode);
    if (isEthnicArea) {
      setDisabled(false);
    } else {
      form.setFieldsValue({
        potatoArea: undefined,
        buckwheatArea: undefined,
        highlandBarleyArea: undefined,
      });
      setDisabled(true);
    }
    const str = r?.map((item) => item.name).join('');
    form.setFieldValue('addr', str);

    // 所属区域变化后，下级乡、村需要重新选择。
    form.setFieldsValue({ countrysideCode: undefined, villageCode: undefined });
    villageRequestId.current += 1;
    setVillageOptions([]);
    setVillageLoading(false);
    loadTownOptions(value?.[1], value?.[2]);
  };

  const onTownChange = (townCode) => {
    form.setFieldValue('villageCode', undefined);
    setVillageOptions([]);
    loadVillageOptions(townCode);
  };

  return (
    <>
      <Modal
        title="耕地面积详情追加"
        maskClosable={false}
        destroyOnClose
        open={open}
        onOk={onOk}
        onCancel={onCancel}
      >
        <Form form={form} {...formItemLayout}>
          <Form.Item hidden name="key">
            <Input />
          </Form.Item>
          <Form.Item hidden name="addr">
            <Input />
          </Form.Item>
          <Form.Item
            name="provinceId"
            label="种粮所属区域"
            rules={[
              {
                required: true,
                message: '请选择种粮所属区域',
              },
            ]}
          >
            <Cascader
              disabled={provinceIdDisabled}
              onChange={onChange}
              options={dict.area}
              placeholder="请选择种粮所属区域"
              fieldNames={{ label: 'name', value: 'id', children: 'children' }}
            />
          </Form.Item>
          <Form.Item
            name="countrysideCode"
            label="种粮地点（乡）"
            rules={[
              {
                required: true,
                message: '请选择种粮地点（乡）',
              },
            ]}
          >
            <Select
              placeholder="请选择种粮地点（乡）"
              loading={townLoading}
              options={townOptions}
              fieldNames={{ label: 'townName', value: 'townCode' }}
              onChange={onTownChange}
            />
          </Form.Item>
          <Form.Item
            name="villageCode"
            label="种粮地点（村）"
            rules={[
              {
                required: true,
                message: '请选择种粮地点（村）',
              },
            ]}
          >
            <Select
              placeholder="请选择种粮地点（村）"
              loading={villageLoading}
              disabled={!countrysideCode}
              options={villageOptions}
              fieldNames={{ label: 'villageName', value: 'villageCode' }}
            />
          </Form.Item>
          <Form.Item
            name="contractedArea"
            label="承包耕地面积"
            rules={[
              {
                required: true,
                message: '请输入承包耕地面积',
              },
            ]}
          >
            <InputNumber placeholder="承包耕地面积" min={0} addonAfter="亩" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item
            name="leasedArea"
            label="租种耕地面积"
            rules={[
              {
                required: true,
                message: '请输入租种耕地面积',
              },
            ]}
          >
            <InputNumber placeholder="租种耕地面积" min={0} addonAfter="亩" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="wheatArea" label="小麦">
            <InputNumber placeholder="小麦" min={0} addonAfter="亩" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="riceArea" label="水稻">
            <InputNumber placeholder="水稻" min={0} addonAfter="亩" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="cornArea" label="玉米">
            <InputNumber placeholder="玉米" min={0} addonAfter="亩" style={{ width: '100%' }} />
          </Form.Item>
          <Form.Item name="potatoArea" label="马铃薯">
            <InputNumber placeholder="马铃薯" min={0} addonAfter="亩" style={{ width: '100%' }} disabled={disabled} />
          </Form.Item>
          <Form.Item name="buckwheatArea" label="荞麦">
            <InputNumber placeholder="荞麦" min={0} addonAfter="亩" style={{ width: '100%' }} disabled={disabled} />
          </Form.Item>
          <Form.Item name="highlandBarleyArea" label="青稞">
            <InputNumber placeholder="青稞" min={0} addonAfter="亩" style={{ width: '100%' }} disabled={disabled} />
          </Form.Item>
        </Form>
      </Modal>
      <Modal
        title="确认所属区域变更"
        open={confirmModalOpen}
        onOk={onConfirmOk}
        onCancel={onConfirmCancel}
        okText="确定"
        cancelText="取消"
        zIndex={1001}
        maskClosable={false}
      >
        <p>详细情况表中区域均会同步为该种粮所属区域，是否确定变更？</p>
      </Modal>
    </>
  );
});
FarmingForm.propTypes = {
  onSubmit: func,
  dict: any,
};

export default FarmingForm;
